import React, { useEffect, useRef, useState } from 'react'
import { FaCheckCircle } from 'react-icons/fa'
import { IoMdNotificationsOutline } from 'react-icons/io'
import { MdOpenInNew } from 'react-icons/md'

import { formatNotificationAge, formatNotificationDate } from './utils'
import { NotificationsFeed, PREVIEW_SIZE } from './useNotifications'

interface IProps {
  feed: Pick<NotificationsFeed, 'items' | 'loading' | 'isRead' | 'markAsRead'>
  /** Abre o modal na lista completa, sem destacar nenhuma notificação. */
  onOpenAll: () => void
  /** Abre o modal já com esta notificação aberta. */
  onOpenNotification: (id: number) => void
}

/**
 * O texto fica preso em duas linhas no painel; quando não cabe inteiro, o item ganha o atalho
 * "Ver tudo" — o corpo completo é assunto do modal.
 */
function useIsTruncated(text: string) {
  const ref = useRef<HTMLParagraphElement>(null)
  const [truncated, setTruncated] = useState(false)

  useEffect(() => {
    const element = ref.current

    if (!element) {
      return
    }

    setTruncated(element.scrollHeight - element.clientHeight > 1)
  }, [text])

  return { ref, truncated }
}

const NotificationItem: React.FC<{
  notification: Notification
  read: boolean
  onOpen: () => void
  onMarkAsRead: () => void
}> = ({ notification, read, onOpen, onMarkAsRead }) => {
  const { ref, truncated } = useIsTruncated(notification.description)

  return (
    <div
      className={`notifications__item ${read ? '' : 'is-unread'}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen()
        }
      }}
    >
      <span className={`notifications__dot ${read ? 'is-hidden' : ''}`} />

      <div className="notifications__item_body">
        <span className="notifications__item_title">{notification.title}</span>

        <p ref={ref} className="notifications__item_text is-clamped">
          {notification.description}
        </p>

        <div className="notifications__item_footer">
          <time
            dateTime={notification.created_at}
            title={formatNotificationDate(notification.created_at)}
          >
            {formatNotificationAge(notification.created_at)}
          </time>

          {truncated && (
            <button
              type="button"
              className="notifications__see_all"
              onClick={event => {
                event.stopPropagation()
                onOpen()
              }}
            >
              Ver tudo
            </button>
          )}
        </div>
      </div>

      {!read && (
        <button
          type="button"
          className="notifications__read"
          title="Marcar como lida"
          aria-label="Marcar como lida"
          onClick={event => {
            event.stopPropagation()
            onMarkAsRead()
          }}
        >
          <FaCheckCircle />
        </button>
      )}
    </div>
  )
}

/** Painel do sino: as notificações mais recentes, em formato curto. */
export const NotificationsPanel: React.FC<IProps> = ({
  feed,
  onOpenAll,
  onOpenNotification
}) => {
  const preview = feed.items.slice(0, PREVIEW_SIZE)
  const unread = preview.filter(
    notification => !feed.isRead(notification)
  ).length

  return (
    <>
      <div className="notifications__header">
        <strong>Notificações</strong>
        {unread > 0 && (
          <span className="notifications__pill">
            {unread} não {unread === 1 ? 'lida' : 'lidas'}
          </span>
        )}
      </div>

      <div className="notifications__list">
        {feed.loading &&
          [0, 1, 2].map(row => (
            <div className="notifications__skeleton" key={row}>
              <div
                className="bg-load"
                style={{ height: '0.9rem', width: '60%' }}
              />
              <div className="bg-load" style={{ height: '0.8rem' }} />
              <div
                className="bg-load"
                style={{ height: '0.8rem', width: '35%' }}
              />
            </div>
          ))}

        {!feed.loading && preview.length === 0 && (
          <div className="notifications__empty">
            <IoMdNotificationsOutline />
            <strong>Nenhuma notificação</strong>
            <span>
              Quando algo acontecer por aqui, você vê primeiro nesta lista.
            </span>
          </div>
        )}

        {!feed.loading &&
          preview.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              read={feed.isRead(notification)}
              onOpen={() => onOpenNotification(notification.id)}
              onMarkAsRead={() => feed.markAsRead(notification.id)}
            />
          ))}
      </div>

      {!feed.loading && preview.length > 0 && (
        <div className="notifications__footer">
          <button type="button" onClick={onOpenAll}>
            <MdOpenInNew /> Ver todas as notificações
          </button>
        </div>
      )}
    </>
  )
}
